import { useMemo } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import { requiresVatNumber } from '@core/constants/platform';
import { useCities, useCountries, useStates } from '@core/hooks/useReference';
import MobileNumberField, {
  mapCountry,
  validateMobileNumber,
} from '@components/base/MobileNumberField';
import { useCopy } from '@/app/providers/useCopy';

const field = (name) => ['business_details', ...[].concat(name)];
const COUNTRY_FIELD = field(['business_address', 'country']);
const STATE_FIELD = field(['business_address', 'state']);
const CITY_FIELD = field(['business_address', 'city']);

// The form keeps the state and city names (what the signup payload has always
// sent); the state code is looked up from the list to fetch its cities.
const toStateOption = (state) => ({
  value: state?.name || '',
  label: state?.name || '',
  code: state?.state_code || state?.code || '',
});
const toCityOption = (city) => {
  const name = city?.name || city?.city_name || city?.cityName || '';
  return { value: name, label: name };
};

/**
 * B2B signup fields, nested under `business_details` in the parent AntD form.
 * Every field is required except the landmark and zip code. The shopper
 * picks the country (no default); a VAT country swaps the GSTIN for the VAT
 * number.
 */
export default function BusinessDetailsFields({ size }) {
  const t = useCopy();
  const required = (message) => [{ required: true, whitespace: true, message }];
  const form = Form.useFormInstance();
  const country = Form.useWatch(COUNTRY_FIELD, form);
  const showVatNumber = requiresVatNumber(country);

  const countries = useCountries();
  const countryOptions = useMemo(
    () =>
      (Array.isArray(countries.data) ? countries.data.map(mapCountry) : [])
        .filter((c) => c.value)
        .map((c) => ({
          value: c.value,
          label: `${c.label.split(' ')[0]} ${c.name}`,
          name: c.name,
        })),
    [countries.data],
  );
  const countryName = countryOptions.find((c) => c.value === country)?.name;

  const state = Form.useWatch(STATE_FIELD, form);
  const states = useStates(country, { countryName });
  const stateOptions = useMemo(
    () => (states.data ?? []).map(toStateOption).filter((s) => s.value),
    [states.data],
  );
  const stateCode = stateOptions.find((s) => s.value === state)?.code;

  const cities = useCities(country, stateCode, { countryName, stateName: state });
  const cityOptions = useMemo(
    () => (cities.data ?? []).map(toCityOption).filter((c) => c.value),
    [cities.data],
  );

  return (
    <fieldset className="business-details-fields">
      <Form.Item
        label={t('businessName', 'Business name')}
        name={field('business_name')}
        rules={required(t('businessNameRequired', 'Business name is required'))}
      >
        <Input
          autoComplete="organization"
          size={size}
          placeholder={t('enterBusinessName', 'Enter business name')}
        />
      </Form.Item>
      <Form.Item
        label={t('businessType', 'Business type')}
        name={field('business_type')}
        rules={required(t('businessTypeRequired', 'Business type is required'))}
      >
        <Input
          size={size}
          placeholder={t('enterBusinessType', 'e.g. Retailer, Wholesaler, Restaurant')}
        />
      </Form.Item>
      <Form.Item
        label={t('businessPhoneNumber', 'Business phone number')}
        name={field('business_phone')}
        required
        rules={[{ validator: (_, value) => validateMobileNumber(value, t) }]}
      >
        <MobileNumberField placeholder={t('enterMobileNumber', 'Enter mobile number')} />
      </Form.Item>
      <Form.Item
        label={t('businessEmail', 'Business email')}
        name={field('business_email')}
        rules={[
          { required: true, message: t('businessEmailRequired', 'Business email is required') },
          { type: 'email', message: t('enterValidEmail', 'Enter a valid email') },
        ]}
      >
        <Input
          type="email"
          autoComplete="email"
          size={size}
          placeholder={t('enterBusinessEmail', 'Enter business email')}
        />
      </Form.Item>
      {/* Business address: line, landmark, country, state, city, zip. The
          state list follows the country and the city list follows the state. */}
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('addressLine', 'Address line')}
            name={field(['business_address', 'line'])}
            rules={required(t('addressLineRequired', 'Address line is required'))}
          >
            <Input
              autoComplete="address-line1"
              size={size}
              placeholder={t('enterAddressLine', 'Street, area')}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('landmark', 'Landmark')}
            name={field(['business_address', 'land_mark'])}
          >
            <Input
              autoComplete="address-line2"
              size={size}
              placeholder={t('enterLandmark', 'Nearby landmark (optional)')}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('country', 'Country')}
            name={COUNTRY_FIELD}
            rules={[{ required: true, message: t('countryRequired', 'Country is required') }]}
          >
            <Select
              showSearch
              size={size}
              loading={countries.isLoading}
              options={countryOptions}
              optionFilterProp="label"
              placeholder={t('selectCountry', 'Select country')}
              // A new country drops the state and city.
              onChange={() => {
                form.setFieldValue(STATE_FIELD, undefined);
                form.setFieldValue(CITY_FIELD, undefined);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('state', 'State')}
            name={STATE_FIELD}
            rules={[{ required: true, message: t('stateRequired', 'State is required') }]}
          >
            <Select
              showSearch
              size={size}
              disabled={!country}
              loading={states.isFetching}
              options={stateOptions}
              optionFilterProp="label"
              placeholder={t('selectState', 'Select state')}
              notFoundContent={states.isFetching ? null : undefined}
              onChange={() => form.setFieldValue(CITY_FIELD, undefined)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('city', 'City')}
            name={CITY_FIELD}
            rules={[{ required: true, message: t('cityRequired', 'City is required') }]}
          >
            <Select
              showSearch
              size={size}
              disabled={!state}
              loading={cities.isFetching}
              options={cityOptions}
              optionFilterProp="label"
              placeholder={t('selectCity', 'Select city')}
              notFoundContent={cities.isFetching ? null : undefined}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('zipCode', 'Zip code')}
            name={field(['business_address', 'zip_code'])}
          >
            <Input
              autoComplete="postal-code"
              size={size}
              placeholder={t('enterZipCodeOptional', 'Zip code (optional)')}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('trn', 'TRN')}
            name={field('trn')}
            rules={required(t('trnRequired', 'TRN is required'))}
          >
            <Input size={size} placeholder={t('enterTrn', 'Enter TRN')} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('crn', 'CRN')}
            name={field('crn')}
            rules={required(t('crnRequired', 'CRN is required'))}
          >
            <Input size={size} placeholder={t('enterCrn', 'Enter CRN')} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={t('serviceTaxNumber', 'Service tax number')}
            name={field('service_tax_number')}
            rules={required(t('serviceTaxNumberRequired', 'Service tax number is required'))}
          >
            <Input
              size={size}
              placeholder={t('enterServiceTaxNumber', 'Enter service tax number')}
            />
          </Form.Item>
        </Col>
        {/* VAT countries collect a VAT number instead of a GSTIN. */}
        {showVatNumber ? null : (
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('gstin', 'GSTIN')}
              name={field('gstin')}
              rules={required(t('gstinRequired', 'GSTIN is required'))}
            >
              <Input size={size} placeholder={t('enterGstin', 'Enter GSTIN')} />
            </Form.Item>
          </Col>
        )}
        {showVatNumber ? (
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('vatNumber', 'VAT number')}
              name={field('vat_number')}
              rules={required(t('vatNumberRequired', 'VAT number is required'))}
            >
              <Input size={size} placeholder={t('enterVatNumber', 'Enter VAT number')} />
            </Form.Item>
          </Col>
        ) : null}
      </Row>
    </fieldset>
  );
}
